import { Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDownloadFatura } from "@/hooks/useDownloadFatura";
import { EnviarFaturaClienteButton } from "@/components/faturas/EnviarFaturaClienteButton";
import type { ContaFixa } from "@/hooks/useContaFixa";

const NOME_SERVICO: Record<string, string> = {
  INTERNET: "Internet", TV: "TV", FIXO: "Telefone fixo", CELULAR: "Celular", ADICIONAL: "Cobranças adicionais", OUTRO: "Outros",
};

const reais = (v: number | null | undefined) =>
  v == null ? "—" : v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dataBr = (iso: string | null | undefined, comAno = true) => {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? (comAno ? `${m[3]}/${m[2]}/${m[1]}` : `${m[3]}/${m[2]}`) : "—";
};
const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
export const mesDaContaFixa = (c: ContaFixa) => {
  const m = String(c.periodo_fim || "").match(/^(\d{4})-(\d{2})/);
  return m ? `${MESES[Number(m[2]) - 1]}/${m[1]}` : "";
};

/** A 1ª conta proporcional: o período não é o mês cheio. */
function proporcional(c: ContaFixa) {
  const i = String(c.periodo_inicio || "").match(/^\d{4}-\d{2}-(\d{2})/);
  return !!i && i[1] !== "01";
}

/**
 * `acao` troca o Enviar (que abre a janela de número e e-mail) pelo botão de quem
 * já sabe para onde a conta vai, como a triagem (Douglas 20/10/2026: "é muita tela
 * abrindo"). `origem` diz de onde a conta veio; sem ela, "lida de imagem".
 */
export function ContaFixaQuadro({ conta, cnpj, razaoSocial, compacto = false, className, acao, origem }: {
  conta: ContaFixa; cnpj?: string | null; razaoSocial?: string | null; compacto?: boolean; className?: string;
  acao?: React.ReactNode; origem?: string;
}) {
  const { downloadFatura } = useDownloadFatura();
  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex flex-nowrap items-center justify-between gap-2">
        <div className="min-w-0 truncate text-[12px] font-semibold" title={`Conta ${conta.conta_numero ?? ""}`}>
          Conta {conta.conta_numero} · {mesDaContaFixa(conta)}
          <span className="ml-2 text-[10.5px] font-normal text-muted-foreground">{origem || "lida de imagem"}</span>
        </div>
        <div className="flex shrink-0 flex-nowrap items-center gap-1.5">
          {conta.arquivo_url && (
            <button type="button" onClick={() => downloadFatura(conta.arquivo_url!)}
              className="inline-flex h-[29px] items-center gap-1.5 whitespace-nowrap rounded-md border border-border px-2.5 text-[11px] text-muted-foreground hover:text-primary focus-visible:ring-2 focus-visible:ring-ring">
              <Download className="h-3.5 w-3.5" /> Ver PDF
            </button>
          )}
          {acao ?? (conta.solicitacao_id && (
            <EnviarFaturaClienteButton solicitacaoId={conta.solicitacao_id} cnpj={cnpj ?? null} razaoSocial={razaoSocial ?? null} competencia={mesDaContaFixa(conta)} />
          ))}
        </div>
      </div>

      {!compacto && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            ["Total da conta", `R$ ${reais(conta.total)}`],
            ["Vencimento", dataBr(conta.vencimento)],
            ["Período", `${dataBr(conta.periodo_inicio, false)} a ${dataBr(conta.periodo_fim, false)}`],
            ["Conta", conta.conta_numero || "—"],
          ].map(([rotulo, valor]) => (
            <div key={rotulo} className="rounded-lg border border-border bg-card px-3 py-2">
              <div className="text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">{rotulo}</div>
              <div className="truncate font-display text-[17px] font-bold tabular-nums" title={valor}>{valor}</div>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-border">
        <div className="grid h-8 grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_76px_minmax(0,1fr)] items-center gap-2 bg-[#FAF8F4] px-3 text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground dark:bg-[#241A1A]">
          <span>Serviço</span><span>Plano</span><span className="text-right">Cobrado</span><span>Fidelidade</span>
        </div>
        {conta.servicos.map((s, i) => (
          <div key={i} className="grid h-11 grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_76px_minmax(0,1fr)] items-center gap-2 border-t border-border px-3 text-[13px] even:bg-[#FBF9F6] dark:even:bg-[#241A1A]">
            <span className="truncate font-semibold" title={NOME_SERVICO[s.servico] || s.servico}>{NOME_SERVICO[s.servico] || s.servico}</span>
            <span className="truncate" title={s.plano || ""}>
              {s.plano || "—"}
              {s.desconto ? <span className="block truncate text-[11px] text-muted-foreground">desconto R$ {reais(s.desconto)}{s.parcela_atual ? ` · parcela ${s.parcela_atual}/${s.parcelas_total}` : ""}</span> : null}
            </span>
            <span className="text-right font-display tabular-nums">{reais(s.valor)}</span>
            <span className="truncate text-muted-foreground">{s.fidelidade_ate ? `até ${dataBr(s.fidelidade_ate)}` : "—"}</span>
          </div>
        ))}
      </div>
      {proporcional(conta) && (
        <p className="text-[11px] text-muted-foreground">
          Conta proporcional: o período vai de {dataBr(conta.periodo_inicio, false)} a {dataBr(conta.periodo_fim, false)}, então o valor é menor que a mensalidade.
        </p>
      )}
    </div>
  );
}
